/**
 * Capa de datos offline con IndexedDB
 *
 * Usa lazy init para evitar abrir la BD hasta que se necesite.
 *
 * @module services/offlineDb
 */

import { openDB } from 'idb';

const DB_NAME = 'app-mantencion-offline';
const DB_VERSION = 2;

let dbPromise = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Ordenes de trabajo
        if (!db.objectStoreNames.contains('ordenesTrabajo')) {
          const otStore = db.createObjectStore('ordenesTrabajo', { keyPath: 'id' });
          otStore.createIndex('estado', 'estado');
          otStore.createIndex('fecha_programada', 'fecha_programada');
        }

        // Actividades
        if (!db.objectStoreNames.contains('actividades')) {
          const actStore = db.createObjectStore('actividades', { keyPath: 'id' });
          actStore.createIndex('orden_trabajo_id', 'orden_trabajo_id');
        }

        // Evidencias
        if (!db.objectStoreNames.contains('evidencias')) {
          const evStore = db.createObjectStore('evidencias', { keyPath: 'id' });
          evStore.createIndex('actividad_mantenimiento_id', 'actividad_mantenimiento_id');
        }

        // Cola de mutaciones
        if (!db.objectStoreNames.contains('mutationQueue')) {
          const mqStore = db.createObjectStore('mutationQueue', { keyPath: 'id', autoIncrement: true });
          mqStore.createIndex('timestamp', 'timestamp');
          mqStore.createIndex('status', 'status');
        }

        // Almacén de archivos
        if (!db.objectStoreNames.contains('fileStore')) {
          db.createObjectStore('fileStore', { keyPath: 'id', autoIncrement: true });
        }

        // v2: Cola de uploads
        if (oldVersion < 2) {
          db.createObjectStore('uploadQueue', { keyPath: 'id' });
        }
      },
      blocked() {
        console.warn('DB bloqueada por otra pestaña');
      },
      blocking(currentVersion, blockedVersion, event) {
        dbPromise = null;
        event.target.close();
      }
    });
  }
  return dbPromise;
}

/**
 * Guarda un array de órdenes de trabajo
 *
 * @param {Array<Object>} items - Órdenes de trabajo
 */
async function saveOrdenesTrabajo(items) {
  const db = await getDb();
  const tx = db.transaction('ordenesTrabajo', 'readwrite');
  for (const item of items) {
    await tx.store.put(item);
  }
  await tx.done;
}

/**
 * Obtiene todas las órdenes de trabajo almacenadas
 *
 * @returns {Promise<Array<Object>>}
 */
async function getOrdenesTrabajo() {
  const db = await getDb();
  return db.getAll('ordenesTrabajo');
}

/**
 * Agrega una mutación a la cola
 *
 * @param {Object} mutation - Datos de la mutación
 * @returns {Promise<number>} ID generado
 */
async function addMutation(mutation) {
  const db = await getDb();
  return db.add('mutationQueue', mutation);
}

/**
 * Obtiene mutaciones pendientes
 *
 * @returns {Promise<Array<Object>>}
 */
async function getPendingMutations() {
  const db = await getDb();
  return db.getAll('mutationQueue');
}

/**
 * Almacena un archivo (blob) en IndexedDB
 *
 * @param {Object} fileData - { file, metadata }
 * @returns {Promise<number>} ID generado
 */
async function storeFile(fileData) {
  const db = await getDb();
  return db.add('fileStore', fileData);
}

/**
 * Obtiene un archivo almacenado
 *
 * @param {number} id - ID del archivo
 * @returns {Promise<Object>}
 */
async function getFile(id) {
  const db = await getDb();
  return db.get('fileStore', id);
}

/**
 * Limpia todos los stores de IndexedDB
 */
async function clearAll() {
  const db = await getDb();
  for (const name of db.objectStoreNames) {
    const tx = db.transaction(name, 'readwrite');
    await tx.store.clear();
    await tx.done;
  }
}

/**
 * Persiste la cola de uploads en IndexedDB
 *
 * @param {Array<Object>} items - Items de la cola
 */
async function saveUploadQueue(items) {
  const db = await getDb();
  const tx = db.transaction('uploadQueue', 'readwrite');
  await tx.store.clear();
  for (const item of items) {
    await tx.store.put({ id: item.id, metadata: item.metadata, status: item.status, retries: item.retries, fileStoreId: item.fileStoreId });
  }
  await tx.done;
}

/**
 * Obtiene todos los items de la cola de uploads
 *
 * @returns {Promise<Array<Object>>}
 */
async function getUploadQueue() {
  const db = await getDb();
  return db.getAll('uploadQueue');
}

const offlineDb = {
  getDb,
  saveOrdenesTrabajo,
  getOrdenesTrabajo,
  addMutation,
  getPendingMutations,
  storeFile,
  getFile,
  clearAll,
  saveUploadQueue,
  getUploadQueue
};

export default offlineDb;
